import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; keys: readonly string[] };
type Slot = readonly [at: number, chord: string];

const CHORDS: Readonly<Record<string, Chord>> = {
  Am9: { root: 'A1', keys: ['G3', 'B3', 'C4', 'E4'] },
  Fmaj9: { root: 'F1', keys: ['E3', 'G3', 'A3', 'C4'] },
  Cmaj9: { root: 'C2', keys: ['E3', 'G3', 'B3', 'D4'] },
  E7b9: { root: 'E2', keys: ['G#3', 'B3', 'D4', 'F4'] },
  Dm9: { root: 'D2', keys: ['F3', 'A3', 'C4', 'E4'] },
  Bbmaj7: { root: 'Bb1', keys: ['A3', 'D4', 'E4', 'F4'] },
  Em9: { root: 'E2', keys: ['G3', 'B3', 'D4', 'F#4'] },
  A7: { root: 'A1', keys: ['G3', 'C#4', 'E4'] },
  G13: { root: 'G1', keys: ['F3', 'B3', 'E4'] },
  Ebmaj7: { root: 'Eb2', keys: ['D3', 'G3', 'Bb3', 'D4'] },
};

const PROGS: readonly (readonly (readonly Slot[])[])[] = [
  [[[0, 'Am9']], [[0, 'Fmaj9']], [[0, 'Cmaj9']], [[0, 'E7b9']], [[0, 'Am9']], [[0, 'Dm9']], [[0, 'Bbmaj7'], [8, 'E7b9']], [[0, 'Am9'], [8, 'E7b9']]],
  [[[0, 'Fmaj9']], [[0, 'Em9']], [[0, 'Dm9']], [[0, 'Cmaj9']], [[0, 'Fmaj9']], [[0, 'Em9'], [8, 'A7']], [[0, 'Dm9']], [[0, 'G13']]],
  [[[0, 'Dm9']], [[0, 'G13']], [[0, 'Cmaj9']], [[0, 'Am9']], [[0, 'Dm9']], [[0, 'G13']], [[0, 'Ebmaj7']], [[0, 'Cmaj9'], [8, 'E7b9']]],
];

const HOOK: Note[][] = [
  [[0, 'E5', 3], [3, 'C5', 1, 0.7], [4, 'B4', 2], [6, 'A4', 4], [12, 'E5', 2], [14, 'G5', 2]],
  [[0, 'A5', 3], [3, 'G5', 1, 0.7], [4, 'E5', 4], [10, 'C5', 2], [12, 'D5', 2], [14, 'E5', 2]],
  [[0, 'G5', 6], [6, 'E5', 2], [8, 'D5', 4], [12, 'B4', 4]],
  [[0, 'G#4', 2], [2, 'B4', 2], [4, 'D5', 2], [6, 'F5', 4], [10, 'E5', 6]],
  [[0, 'C6', 3], [3, 'B5', 1, 0.7], [4, 'A5', 2], [6, 'E5', 4], [12, 'G5', 2], [14, 'A5', 2]],
  [[0, 'F5', 4], [4, 'E5', 2], [6, 'D5', 2], [8, 'A5', 6], [14, 'G5', 2]],
  [[0, 'E5', 3], [3, 'D5', 3], [6, 'A4', 2], [8, 'G#4', 4], [12, 'B4', 2], [14, 'D5', 2]],
  [[0, 'C5', 8], [8, 'B4', 2], [10, 'E5', 6]],
];

const KEYS = [0, -2, 3, -4, 2, 5, -3] as const;
const NATURAL = [0, 2, 4, 5, 7, 9, 11] as const;
const KICKS = [
  ['X.....x...X.....', 'X.........X...x.'],
  ['X.......X.x.....', 'X.....x.X.......'],
  ['X.....x...x.....', 'X.........X.....'],
] as const;
const SNARE = '....X.......X...';
const TICK = 'x-o-x-o-x-o-x-o-';
const SWIRL = 'o...o...o...o...';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(CHUVA, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((bar) => bar.map(([at, name]) => ({ at, ...CHORDS[name]! })));
  const kicks = ch.pick(KICKS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((bar) => bar.map((c) => ({ at: c.at, tones: pcs([c.root, ...c.keys]) }))),
        NATURAL,
        [69, 88],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 31) m += 12;
    while (m > 42) m -= 12;
    return noteName(m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 56, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outroBars = form[form.length - 1]!.bars;
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const rng = createRng(`chuva-${seed}`);
  const human = (v: number) => v * (0.94 + rng.float() * 0.12);
  const drift = () => (rng.float() - 0.5) * 0.012;

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number, t = g.at(bar, step)) => events.push({ v, t, bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number, t = g.at(bar, step)) =>
    events.push({ v, t, bar, step, vel, dur, note });
  const chord = (bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v: 'keys', t: g.at(bar, step) + drift(), bar, step, vel, dur, notes, strum: 0.014 });
  const pingos = (bar: number, count: number, tones: readonly string[]) => {
    for (let k = 0; k < count; k++) {
      const step = rng.int(0, 16);
      const note = transpose(tones[rng.int(0, tones.length)]!, 24);
      play('pingo', bar, step, note, s * 1.5, 0.2 + rng.float() * 0.25, g.at(bar, step) + rng.float() * s * 0.8);
    }
  };

  const autos: Score['autos'] = [
    { v: 'keysLp', points: [[g.at(0), 800], [g.at(intro), 3200]] },
    { v: 'keysLp', points: [[g.at(total - outroBars / 2), 3200], [g.at(total) + 2, 600]] },
  ];
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    autos.push({ v: 'rainLp', points: [[g.at(bar - 1), 3600], [g.at(bar), 9000], [g.at(bar + 2), 3600]] });
    events.push({ v: 'gust', t: g.at(bar - 1), bar: bar - 1, step: 0, vel: 1, end: g.at(bar) });
  }

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 8);
    const end = b.bar === total - 1;
    const bar = prog[i]!;
    const tones = bar[0]!.keys.map(tr);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const gap = b.last && (b.bar + 1 === drop1 || b.bar + 1 === drop2);
    const band = !gap && (drop || b.name === 'A' || (b.name === 'outro' && b.index < b.bars / 2));

    if (end) {
      const c = prog[0]![0]!;
      hit('kick', b.bar, 0, 0.8);
      chord(b.bar, 0, c.keys.map(tr), g.barLength * 1.5, 0.75);
      play('bass', b.bar, 0, low(c.root), g.barLength * 0.6, 0.85);
      pingos(b.bar, 3, c.keys.map(tr));
      continue;
    }

    if (gap) {
      pingos(b.bar, 4 + Math.round(e * 4), tones);
      if (e >= 0.75 && b.bar + 1 === drop2) hit('trovao', b.bar, 0, 0.8);
      continue;
    }

    for (const c of bar) {
      const keys = c.keys.map(tr);
      if (bar.length === 1) {
        chord(b.bar, 0, keys, s * 9, human(b.name === 'intro' ? 0.6 : 0.72));
        if (b.name !== 'intro') chord(b.bar, drop ? 6 : 10, keys.slice(1), s * 4, human(0.42));
      } else {
        chord(b.bar, c.at, keys, s * 7, human(0.7));
      }
    }

    if (band) {
      const pair = kicks[b.index % 2]!;
      for (const h of hits(pair)) hit('kick', b.bar, h.step, human(h.vel));
      for (const h of hits(SNARE)) hit('snare', b.bar, h.step, human(0.95));
      if (b.index % 4 === 3) hit('ghost', b.bar, 15, 0.35);
      hit('ghost', b.bar, 7, 0.25);
      if (drop || e >= 0.65) for (const h of hits(TICK)) hit('tick', b.bar, h.step, human(h.vel));
      for (const h of hits(SWIRL)) hit('swirl', b.bar, h.step, human(h.vel));
      for (const c of bar) {
        const root = low(c.root);
        play('bass', b.bar, c.at, root, s * 3.5, human(0.92));
        if (bar.length === 1) {
          play('bass', b.bar, 6, transpose(root, 7), s * 2, human(0.72));
          play('bass', b.bar, 10, root, s * 3, human(0.8));
        }
      }
      if (!b.last) play('bass', b.bar, 14, noteName(midi(low(prog[cycle(i + 1, 8)]![0]!.root)) - 1), s * 1.6, human(0.6));
    } else if (b.name === 'break') {
      if (b.index % 2 === 0) hit('kick', b.bar, 0, 0.6);
      for (const h of hits(SWIRL)) hit('swirl', b.bar, h.step, human(h.vel * 0.8));
      play('bass', b.bar, 0, low(bar[0]!.root), s * 6, 0.75);
      if (b.index === 0 && e >= 0.75) hit('trovao', b.bar, 0, 0.9);
      pingos(b.bar, 2 + Math.round(e * 3), tones);
    } else if (b.name === 'intro') {
      pingos(b.bar, 3 + Math.round(e * 3), tones);
      if (b.index >= b.bars / 2) for (const h of hits(SWIRL)) hit('swirl', b.bar, h.step, human(h.vel * 0.7));
      if (b.last) hit('ghost', b.bar, 14, 0.4);
    } else if (b.name === 'outro') {
      play('bass', b.bar, 0, low(bar[0]!.root), s * 5, 0.7);
      pingos(b.bar, 3, tones);
    }

    const row = hook[i]!;
    const lead = (vel: number, octave: boolean) => {
      for (const [step, note, len, v] of row) {
        const n = tr(note);
        const t = g.at(b.bar, step) + drift();
        play('lead', b.bar, step, n, len * s * 0.9, human((v ?? 0.9) * vel), t);
        if (octave) play('lead', b.bar, step, transpose(n, -12), len * s * 0.85, (v ?? 0.9) * vel * 0.4, t + 0.004);
      }
    };
    if (b.name === 'A' && e >= 0.65) lead(0.65, false);
    if (b.name === 'drop') lead(0.95, e >= 0.85);
    if (b.name === 'drop2') lead(1, e >= 0.7);
    if (b.name === 'outro' && b.index < b.bars / 2) lead(0.7, false);
  }

  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('land', bar, 0, 1);
  }

  return {
    id: trackId(CHUVA, seed, p),
    template: CHUVA.id,
    seed,
    params: p,
    title: ch.orig ? CHUVA.ref.title : titleOf(seed),
    genre: CHUVA.genre,
    bpm: p.bpm,
    swing: 56,
    lufs: -14,
    key,
    form,
    mix: {
      reverbDecay: 2.6,
      delayTime: (60 / p.bpm) * 0.75,
      delayFeedback: 0.3,
      drive: 1.2,
      softness: 1.6,
      masterLowpass: 9500,
      pumpDepth: 0.15,
      pumpRelease: 0.3,
    },
    events,
    autos,
    accents: { land: 'drop', kick: 'kick', snare: 'snare' },
  };
}

export const CHUVA: Template = {
  id: 'chuva',
  genre: 'lo-fi na chuva',
  bpm: [96, 124],
  ref: { seed: 'garoa', title: 'Garoa', params: { bpm: 116, bars: 28, energy: 0.55 } },
  score,
};
